import axios from "axios";

const API_BASE =
  process.env.REACT_APP_BACKEND_URL ||
  process.env.REACT_APP_API_URL ||
  "";

const TOKEN_KEYS = [
  "pps_admin_token",
  "admin_access_token",
];

export const http = axios.create({
  baseURL: API_BASE,
  headers: {
    "Content-Type": "application/json",
  },
});

http.interceptors.request.use(
  (config) => {
    let token = null;

    for (const key of TOKEN_KEYS) {
      const value = localStorage.getItem(key);

      if (value) {
        token = value;
        break;
      }
    }

    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

http.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      localStorage.removeItem("pps_admin_token");
      localStorage.removeItem("admin_access_token");
      localStorage.removeItem("admin_user");
    }

    return Promise.reject(error);
  }
);

function unwrap(response) {
  return response?.data;
}

function getErrorMessage(error) {
  return (
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.message ||
    "Request failed"
  );
}

export async function fetchResources(params = {}) {
  try {
    const response = await http.get("/api/resources", { params });
    return unwrap(response) || [];
  } catch (error) {
    console.error("fetchResources:", getErrorMessage(error));
    throw error;
  }
}

export async function fetchResource(resourceId) {
  if (!resourceId) {
    throw new Error("Resource ID is required");
  }

  try {
    const response = await http.get(
      `/api/resources/${encodeURIComponent(resourceId)}`
    );

    return unwrap(response);
  } catch (error) {
    console.error("fetchResource:", getErrorMessage(error));
    throw error;
  }
}

export async function updateResource(resourceId, data) {
  if (!resourceId) {
    throw new Error("Resource ID is required");
  }

  try {
    const response = await http.patch(
      `/api/resources/${encodeURIComponent(resourceId)}`,
      data
    );

    return unwrap(response);
  } catch (error) {
    console.error("updateResource:", getErrorMessage(error));
    throw error;
  }
}

export async function toggleStar(resourceOrId, starred) {
  const resourceId =
    typeof resourceOrId === "object"
      ? resourceOrId?.id
      : resourceOrId;

  if (!resourceId) {
    throw new Error("Resource ID is required");
  }

  return updateResource(resourceId, {
    starred: Boolean(starred),
  });
}

export async function fetchStats() {
  try {
    const response = await http.get("/api/stats");
    return unwrap(response) || {};
  } catch (error) {
    console.error("fetchStats:", getErrorMessage(error));
    throw error;
  }
}

export async function fetchActivity(limit = 10) {
  try {
    const response = await http.get("/api/activity", {
      params: { limit },
    });

    return unwrap(response) || [];
  } catch (error) {
    console.error("fetchActivity:", getErrorMessage(error));
    throw error;
  }
}

export async function logActivity(
  resourceIdOrPayload,
  action = "opened"
) {
  let payload;

  if (
    resourceIdOrPayload &&
    typeof resourceIdOrPayload === "object"
  ) {
    payload = {
      resource_id: resourceIdOrPayload.resource_id,
      action:
        resourceIdOrPayload.action ||
        action ||
        "opened",
    };
  } else {
    payload = {
      resource_id: resourceIdOrPayload,
      action: action || "opened",
    };
  }

  if (!payload.resource_id) {
    throw new Error("Resource ID is required");
  }

  try {
    const response = await http.post(
      "/api/activity",
      payload
    );

    return unwrap(response);
  } catch (error) {
    console.error("logActivity:", getErrorMessage(error));
    throw error;
  }
}

export async function fetchCategories() {
  try {
    const response = await http.get("/api/categories");

    return (
      unwrap(response) || {
        categories: [],
      }
    );
  } catch (error) {
    console.error(
      "fetchCategories:",
      getErrorMessage(error)
    );

    throw error;
  }
}

const api = {
  http,
  fetchResources,
  fetchResource,
  updateResource,
  toggleStar,
  fetchStats,
  fetchActivity,
  logActivity,
  fetchCategories,
};

export default api;